/** Whose account this is, while an operator is acting for a customer.
 *
 * An operator with a support session open is looking at the customer's real
 * pages, with the customer's real buttons, and every one of them does the
 * real thing. That is the point — and it is exactly why it must be
 * impossible to forget whose account is on screen. So this sits above
 * everything, in a colour nothing else uses, and says who, for how much
 * longer, and how to stop.
 */

import { useEffect, useState } from 'react'
import { acting, type ActingAs } from '../lib/api'
import { support } from '../lib/supportApi'

function minutesLeft(session: ActingAs): number {
  return Math.max(
    0,
    Math.round((new Date(session.expires_at).getTime() - Date.now()) / 60_000),
  )
}

export default function ActingBanner() {
  const [session, setSession] = useState<ActingAs | null>(acting.current())
  const [, tick] = useState(0)

  useEffect(() => {
    // `subscribe` returns Set.delete, whose boolean is not a cleanup function.
    const unsubscribe = acting.subscribe(() => setSession(acting.current()))
    return () => {
      unsubscribe()
    }
  }, [])

  // The remaining time is the thing an operator needs to see changing; a
  // session that has run out stops working mid-task otherwise.
  useEffect(() => {
    if (!session) return
    const timer = setInterval(() => tick((n) => n + 1), 30_000)
    return () => clearInterval(timer)
  }, [session])

  if (!session) return null

  const left = minutesLeft(session)
  const end = async () => {
    // Revoke server-side first; if that fails the local session is still
    // dropped, because a banner that will not go away is worse than a
    // support row that expires on its own in half an hour.
    try {
      await support.stopSession(session.id)
    } catch {
      /* expired or already revoked */
    }
    acting.set(null)
  }

  return (
    <div className="acting-banner" role="status">
      <span>
        <strong>Acting as {session.customer_email}</strong> — every action here
        is theirs, and is recorded against you.
      </span>
      <span className="tiny">
        {left > 0 ? `${left} min left` : 'expired — end and reopen'}
      </span>
      <button type="button" className="btn" onClick={() => void end()}>
        Stop acting
      </button>
    </div>
  )
}
